/**
 * `badge.*` — THE colour table for every lozenge in the app.
 *
 * Split out of `semanticTokens.ts` when this change pushed that file back over
 * the 1000-line CI ceiling, the same way `semanticTokens.interactive.ts` was.
 * Being its own file suits it: `theme/components.ts` builds the Badge variants
 * by interpolating a tone name into these three roles and reads nothing else,
 * so this really is the one place a badge's colour is decided.
 *
 * WHAT BELONGS HERE: a fill, ink or ring a Badge paints on itself. What does
 * NOT: the tints a SURFACE uses (`bg.muted`, `bg.success`) and the app-wide
 * inks (`text.secondary`) — the rows below alias those deliberately, or copy
 * them deliberately, and the comments say which and why.
 *
 * Pinned by the badge tone matrix, the ring floor and the ring-direction guard
 * in `__tests__/contrast.test.ts`, and by `__tests__/badgeVariantResolution.test.tsx`.
 */

import { DARK_DERIVED } from './colors.brand';

export const badgeTokens = {
  // The `bg`/`fg` pairs are the values the Badge variants already shipped,
  // moved rather than re-chosen: every one is pinned at >=4.5:1 in both
  // modes by the badge tone matrix in __tests__/contrast.test.ts, and
  // re-deriving them would have thrown that away for a shape change.
  //
  // FILLS are copied, INKS are aliased, and that asymmetry is deliberate.
  // `badge.neutral.bg` and `badge.brand.bg` hold the same literal values as
  // `bg.muted` and `brand.muted` rather than pointing at them, because a
  // badge restyle must not require editing an app-wide surface token — and,
  // read the other way, retuning a surface must not silently drag every
  // badge with it. The inks DO alias (`text.secondary`, `text.accent`):
  // badge text should follow the app's ink, and the contrast matrix
  // re-measures the pair either way, so an ink retune cannot break a badge
  // quietly. If you change `bg.muted` and want the badge to follow, change
  // it here too — that is the cost of owning the fill, and it is the one
  // this table was created to pay.
  //
  // `border` is the new role. A badge is a fill with a hairline now (the
  // 2026-09 lozenge pass) rather than a bare fill, which is what lets the
  // fill stay quiet enough to sit inside running text without shouting.
  // The ring is one perceptible step off its own fill, in the same hue:
  // DARKER than the fill in light mode, and LIGHTER in dark, where a deep
  // fill can only show an edge by lifting. (Both directions are "a step
  // further from the page", which is the rule; on these ramps that is a
  // HIGHER step number in light and a lower one in dark.) Which step it
  // lands on is measured per tone, not a fixed offset — the ring case in
  // __tests__/contrast.test.ts holds every one to a floor AND to this
  // direction, so the sentence cannot drift from the values again. The ring
  // is decorative: the fill already identifies the badge, so 1.4.11's 3:1
  // does not apply and it is deliberately quiet.
  'badge.neutral.bg': {
    default: 'neutral.200',
    _dark: DARK_DERIVED.surface,
  },
  'badge.neutral.fg': {
    default: 'text.secondary',
    _dark: 'text.secondary',
  },
  // neutral.350, not neutral.300: 300 IS `border.default`, and against a
  // neutral.200 fill the two are 1.03:1 apart — a ring nobody can see.
  'badge.neutral.border': {
    default: 'neutral.350',
    _dark: DARK_DERIVED.border,
  },

  'badge.brand.bg': {
    default: 'accent.50',
    _dark: 'rgba(92, 136, 218, 0.18)',
  },
  'badge.brand.fg': {
    default: 'text.accent',
    _dark: 'text.accent',
  },
  // NOT `brand.mutedBorder` (16% alpha). That token is tuned for a large
  // chat bubble, where a whisper of an edge is enough; at badge size the
  // same alpha disappears. Same hue, raised to hold an edge at 20px tall.
  'badge.brand.border': {
    default: 'accent.200',
    _dark: 'rgba(92, 136, 218, 0.45)',
  },

  'badge.success.bg': {
    default: 'success.100',
    _dark: 'success.800',
  },
  'badge.success.fg': {
    default: 'text.success',
    _dark: 'text.success',
  },
  'badge.success.border': {
    default: 'success.200',
    _dark: 'success.600',
  },

  'badge.warning.bg': {
    default: 'warning.100',
    // 900, not 800. warning.800 is only 3.98:1 against `text.warning`
    // (warning.400), and a badge is nowhere near WCAG large text — 10px at
    // weight 400 — so the 4.5:1 normal-text floor applies. 900 gives 6.81:1.
    _dark: 'warning.900',
  },
  'badge.warning.fg': {
    default: 'text.warning',
    _dark: 'text.warning',
  },
  // 300 in light and 800 in dark, where every other tone is 200/600-700.
  // The warm ramp's 100 -> 200 step is unusually tight (1.10:1, under the
  // perceptible floor the ring guard holds every tone to) and its 900 -> 700
  // unusually wide (2.80:1, nearly a border rather than a hairline). The
  // rule is a measured step off the fill, NOT a fixed ramp offset — see the
  // ring case in __tests__/contrast.test.ts, which is what caught both.
  'badge.warning.border': {
    default: 'warning.300',
    _dark: 'warning.800',
  },

  'badge.error.bg': {
    default: 'danger.100',
    // danger.800 is only 3.90:1 against `text.error` (danger.400); 900
    // gives 5.40:1. Same reasoning as the warning fill above.
    _dark: 'danger.900',
  },
  'badge.error.fg': {
    default: 'text.error',
    _dark: 'text.error',
  },
  'badge.error.border': {
    default: 'danger.200',
    _dark: 'danger.700',
  },

  // `info` is a QUIET NEUTRAL in this theme, not a blue — the accent is
  // reserved for sparkle moments. So its ring comes off the neutral ramp
  // too; `border.info` (info.200) would have painted a blue hairline
  // around a grey fill.
  'badge.info.bg': {
    default: 'neutral.300',
    _dark: DARK_DERIVED.chromeActive,
  },
  'badge.info.fg': {
    default: 'text.info',
    _dark: 'text.info',
  },
  'badge.info.border': {
    default: 'neutral.400',
    _dark: DARK_DERIVED.border,
  },
} as const;
